import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useGetSavedSearches } from "../../../hooks/useSavedSearch";
import type { SavedSearchModel } from "../../../api/savedSearch";

export interface SavedSearchSelectProps {
  readonly customerId: string;
  readonly userId: string;
  readonly selectedId: string | null;
  readonly onLoad: (id: string) => void;
  readonly onClear: () => void;
}

export default function SavedSearchSelect({
  customerId,
  userId,
  selectedId,
  onLoad,
  onClear,
}: SavedSearchSelectProps) {
  const { t } = useTranslation(["search"]);
  const { data } = useGetSavedSearches(customerId, userId);

  const options = useMemo<SavedSearchModel[]>(() => {
    const list = data ?? [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const selected = options.find((o) => o.savedSearchId === selectedId) ?? null;

  return (
    <Autocomplete<SavedSearchModel, false, false, false>
      size="small"
      options={options}
      value={selected}
      isOptionEqualToValue={(o, v) => o.savedSearchId === v.savedSearchId}
      getOptionLabel={(o) => o.name}
      onChange={(_, value, reason) => {
        if (reason === "clear") {
          onClear();
          return;
        }
        if (value) onLoad(value.savedSearchId);
      }}
      sx={{
        minWidth: 200,
        "& .MuiOutlinedInput-root": {
          fontSize: "0.75rem",
          borderRadius: "6px",
          paddingY: 0,
          paddingLeft: "10px",
        },
        "& .MuiOutlinedInput-input": {
          paddingY: "5px !important",
          paddingLeft: "0 !important",
        },
        "& .MuiAutocomplete-endAdornment": {
          top: "50%",
          transform: "translateY(-50%)",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t("search:savedSearch.v2.loadPlaceholder")}
        />
      )}
    />
  );
}
